import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart, formatAUD } from "@/context/CartContext";

const ProductCard = ({ product, onQuickView, index }) => {
  const { add, setOpen } = useCart();
  const colors = product.colors || [];
  const images = colors.length > 0
    ? colors.map((c) => ({ src: c.image || product.image, name: c.name }))
    : [{ src: product.image, name: null }];

  const [slide, setSlide] = useState(0);
  const [hovering, setHovering] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (hovering || images.length < 2) return;
    timer.current = setInterval(() => setSlide((s) => (s + 1) % images.length), 3400);
    return () => clearInterval(timer.current);
  }, [hovering, images.length]);

  const quickAdd = (e) => {
    e.stopPropagation();
    add(product, 1, colors.length > 0 ? colors[slide % colors.length].name : null);
    toast.success(`${product.name} added to cart`, {
      action: { label: "View Cart", onClick: () => setOpen(true) },
    });
  };

  const active = images[slide % images.length];

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.08 }}
      onClick={() => onQuickView(product)}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className="group cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-sm bg-white border border-taupe/30 aspect-square">
        <AnimatePresence mode="wait">
          <motion.img
            key={active.src}
            src={active.src}
            alt={product.name}
            loading="lazy"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        </AnimatePresence>

        {product.badge && (
          <span
            data-testid={`product-badge-${product.id}`}
            className="absolute top-4 left-4 z-10 bg-gold text-charcoal text-[10px] tracking-[0.2em] uppercase font-semibold px-3 py-1.5 rounded-full"
          >
            {product.badge}
          </span>
        )}

        {images.length > 1 && (
          <div data-testid={`card-dots-${product.id}`} className="absolute bottom-4 left-4 z-10 flex items-center gap-1.5">
            {images.map((img, i) => (
              <button
                key={img.name || i}
                data-testid={`card-dot-${product.id}-${i}`}
                onClick={(e) => { e.stopPropagation(); setSlide(i); }}
                aria-label={`Show colour ${img.name}`}
                className={`rounded-full transition-all duration-300 ${
                  i === slide % images.length ? "w-4 h-2 bg-gold" : "w-2 h-2 bg-charcoal/25 hover:bg-charcoal/50"
                }`}
              />
            ))}
          </div>
        )}

        <button
          data-testid={`quick-add-${product.id}`}
          onClick={quickAdd}
          aria-label={`Quick add ${product.name} to cart`}
          className="absolute bottom-4 right-4 z-10 w-12 h-12 rounded-full bg-gold text-charcoal flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 hover:bg-gold-dark active:scale-90 shadow-[0_8px_24px_rgba(212,175,55,0.4)]"
        >
          <Plus size={20} strokeWidth={2} />
        </button>
      </div>

      <div className="pt-5 pb-2">
        <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-1.5">{product.category}</p>
        <h3 className="font-serif-lux text-2xl text-charcoal leading-tight">{product.name}</h3>
        <p className="text-sm text-charcoal/50 font-light mt-1">{product.tagline}</p>
        <div className="flex items-baseline gap-3 mt-3">
          <span data-testid={`product-price-${product.id}`} className="text-base font-medium text-charcoal">
            {formatAUD(product.price)}
          </span>
          {product.compare_at && (
            <span className="text-sm text-charcoal/40 line-through">{formatAUD(product.compare_at)}</span>
          )}
        </div>
      </div>
    </motion.article>
  );
};

export default ProductCard;
